import { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { EmailTemplatesApi, type EmailTemplateRow } from "../api/emailTemplates";
import { EmailsApi } from "../api/emails";
import logger from "../analytics/logger";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  ids: string[];
  onSent: () => void;
};

/** Send a stored email template to the currently selected creators. The send
 * is queued server-side; per-recipient delivery happens asynchronously. */
export default function BulkSendEmailModal({ isOpen, onClose, ids, onSent }: Props) {
  const toast = useToast();
  const [templates, setTemplates] = useState<EmailTemplateRow[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    void (async () => {
      setError(null);
      setTemplateName("");
      setLoading(true);
      try {
        const rows = await EmailTemplatesApi.list();
        if (!active) return;
        setTemplates(rows.filter((t) => t.active));
      } catch (e) {
        if (!active) return;
        logger.error("email_templates_list_failed", e);
        setError("Failed to load templates");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isOpen]);

  const send = async () => {
    if (!templateName) {
      setError("Select a template");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await EmailsApi.send({ template_name: templateName, creator_ids: ids });
      toast({
        title: `Queued ${res.queued} email${res.queued === 1 ? "" : "s"}`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onSent();
      onClose();
    } catch (e: unknown) {
      logger.error("emails_send_failed", e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to send";
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Send email to {ids.length} creator{ids.length === 1 ? "" : "s"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error && (
            <Alert status="error" mb={3}>
              <AlertIcon />
              {error}
            </Alert>
          )}
          <Stack spacing={3}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Template
              </Text>
              <Select
                size="sm"
                placeholder={loading ? "Loading…" : "Select a template"}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                isDisabled={loading}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name} — {t.subject}
                  </option>
                ))}
              </Select>
            </Box>
            {!loading && templates.length === 0 && (
              <Text fontSize="sm" color="gray.500">
                No active templates. Create one on the Email templates page.
              </Text>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose} isDisabled={sending}>
            Cancel
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={send}
            isLoading={sending}
            isDisabled={!templateName}
          >
            Send
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
